import { inject, Injector, runInInjectionContext, Signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { DomSanitizer } from '@angular/platform-browser';

import {
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  switchMap,
} from 'rxjs';

import { filterBoolean } from '@core/common-legacy';
import { injectReportsAppKind, injectReportsTranslations } from '@core/reports-contracts';
import {
  ReportDashboardComponentViewData,
  ReportsAppKind,
  ReportsComponentValueDecorator,
} from '@core/reports-types';
import { buildVisualizationData } from '@core/reports-ui';
import { buildApiDataParams } from '@core/reports-util';
import { DurationPipe } from '@core/user-interface-legacy';

import { ReportsConfigurationFacade } from './reports-configuration.facade';
import { ReportsResultsFacade } from './results.facade';

export function withTranslationReactivity<T>(
  factory: () => T,
  secondaryGroup: string | null | undefined,
  filterGroupTranslations$: Observable<Record<string, string>>,
): Observable<T> {
  if (!secondaryGroup?.includes(':')) {
    return of(factory());
  }
  return filterGroupTranslations$.pipe(
    map(t => t[secondaryGroup] ?? null),
    distinctUntilChanged(),
    map(() => factory()),
  );
}

export const useReportsComponentFullDataStreams = (
  component$: Observable<ReportDashboardComponentViewData>,
  options?: {
    componentDecorator?: ReportsComponentValueDecorator;
  },
) => {
  const { componentDecorator } = options ?? {};
  const resultsFacade = inject(ReportsResultsFacade);
  const sanitizer = inject(DomSanitizer);
  const translations = injectReportsTranslations();
  const durationPipe = new DurationPipe(translations.i18n);
  const clampTrendEndToToday = injectReportsAppKind({ optional: true }) === ReportsAppKind.Ats;
  const configFacade = inject(ReportsConfigurationFacade);
  const metric$ = component$.pipe(
    filterBoolean,
    switchMap(component => configFacade.getMetricBySlug(component.metric)),
    shareReplay(1),
  );

  const params$ = combineLatest([component$, metric$]).pipe(
    map(([component, metric]) => buildApiDataParams(component, metric)),
    distinctUntilChanged(resultsFacade.areParamsEqual),
    shareReplay(1),
  );

  const results$ = params$.pipe(
    switchMap(params => resultsFacade.fetchAndGetResultsContinously$(params).pipe(startWith(null))),
    distinctUntilChanged((a, b) => a?.checksum === b?.checksum),
    shareReplay(1),
  );

  const resultsMeta$ = params$.pipe(
    switchMap(params => resultsFacade.getResultsMeta(params)),
    shareReplay(1),
  );

  const visData$ = combineLatest([component$, metric$, results$]).pipe(
    switchMap(([c, metric, results]) => {
      if (!results || !c || !metric) {
        return of(null);
      }
      const component = componentDecorator ? componentDecorator(c, metric) : c;
      const dataInput = {
        component,
        metric,
        meta: {},
        data: results,
      };
      return withTranslationReactivity(
        () =>
          buildVisualizationData(dataInput, {
            sanitizer,
            translations,
            durationPipe,
            clampTrendEndToToday,
          }),
        component.secondaryGroup,
        translations.filterGroupTranslations$,
      );
    }),
    shareReplay(1),
  );

  return {
    params$,
    results$,
    resultsMeta$,
    visData$,
  };
};

export const useReportsComponentFullData = (
  component: Signal<ReportDashboardComponentViewData | null>,
) => {
  const injector = inject(Injector);
  const component$ = toObservable(component).pipe(filterBoolean);
  const { params$, results$, resultsMeta$, visData$ } = runInInjectionContext(injector, () =>
    useReportsComponentFullDataStreams(component$),
  );

  return {
    params: toSignal(params$),
    results: toSignal(results$),
    resultsMeta: toSignal(resultsMeta$),
    visData: toSignal(visData$),
  };
};
